import { faPartyHorn, faPinata } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CSSProperties } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

type FAIconStyle = CSSProperties & { [key: `--fa-${string}`]: string };

// "Light Pairs" letter pattern: two letters per color, cycling
// pink-magenta → purple-light → teal-aqua across each word. Hand-mixed pastels
// sit one stop lighter than the kawaii palette's `*-main` tones so they read
// against the saturated-purple chip.
const pairColors = ['#f0b0e0', '#dac4f5', '#b8e0e8'] as const;

// The frosted chip's saturated-purple backdrop keeps the pastel letters legible
// regardless of what sits behind the header/card.
const partyHornStyle: FAIconStyle = {
  width: 26,
  height: 26,
  '--fa-primary-color': '#ffffff',
  '--fa-secondary-color': '#ffc7da',
  '--fa-secondary-opacity': '1',
};
const pinataStyle: FAIconStyle = {
  width: 26,
  height: 26,
  '--fa-primary-color': '#ffffff',
  '--fa-secondary-color': '#d9c5ff',
  '--fa-secondary-opacity': '1',
};

const toPairs = (text: string): Array<string> => {
  const pairs: Array<string> = [];
  for (let index = 0; index < text.length; index += 2) pairs.push(text.slice(index, index + 2));
  return pairs;
};

/** One brand word rendered in the Light Pairs pattern. Decorative — the
 *  wordmark as a whole is labelled on the root, so the letters are aria-hidden. */
const BrandWord = ({ text }: { text: string }) => (
  <>
    {toPairs(text).map((pair, pairIndex) => (
      <span
        key={pairColors[pairIndex % pairColors.length]}
        style={{ color: pairColors[pairIndex % pairColors.length] }}
      >
        {pair}
      </span>
    ))}
  </>
);

export interface BrandWordmarkProps {
  /** Extra layout classes merged onto the chip (e.g. responsive show/hide). */
  className?: ClassName;
}

/** The full "🎉 Trippy Planny 🪅" wordmark: a frosted chip carrying the
 *  party-horn, the pastel Light Pairs lettering, and the piñata. Shared by the
 *  app header and the unauthenticated auth screens so the brand stays in one
 *  place. */
export const BrandWordmark = ({ className }: BrandWordmarkProps) => (
  <span
    role="img"
    aria-label="Trippy Planny"
    className={twx(
      'inline-flex items-center gap-2.5 shrink-0 whitespace-nowrap overflow-hidden rounded-xl border border-white/15 bg-secondary-focal/60 px-3 h-12 font-diary text-3xl font-semibold backdrop-blur-md',
      className,
    )}
  >
    <FontAwesomeIcon icon={faPartyHorn} style={partyHornStyle} />
    <span aria-hidden={true}>
      <BrandWord text="Trippy" /> <BrandWord text="Planny" />
    </span>
    <FontAwesomeIcon icon={faPinata} style={pinataStyle} />
  </span>
);
